import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Heading, Input, LoadingSpinner, Text } from '../components/ui'
import { dashcorretorService } from '../services/dashcorretor'
import { portaisAtendimentoService } from '../services/portaisAtendimento'
import { logPortaisAtendimentoUpdate } from '../utils/portaisAtendimentoLogs'
import { buildClassificationColorMap, getClassificationCardStyleFromMap } from '../utils/classificationTrend'
import {
  PORTAIS_ATENDIMENTO_FILA_LIGACAO_CATEGORIAS_KEY,
  PORTAIS_ATENDIMENTO_FILA_LIGACAO_PRODUCAO_MINIMA_KEY,
  parseFilaLigacaoProducaoMinima,
} from '../types/portaisAtendimento'
import type { DashCorretorClassificationRef } from '../types/dashcorretor'

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Erro inesperado. Tente novamente.'
}

function normalizeCategoryList(names: string[]): string[] {
  return Array.from(new Set(names.map((name) => name.trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, 'pt-BR')
  )
}

function areCategoryListsEqual(a: string[], b: string[]): boolean {
  const normA = normalizeCategoryList(a)
  const normB = normalizeCategoryList(b)
  if (normA.length !== normB.length) return false
  return normA.every((value, index) => value === normB[index])
}

function formatProducaoInput(value: number | null): string {
  return value == null ? '' : String(value)
}

const PortaisAtendimentoFilaAutomaticaTab: React.FC = () => {
  const [classifications, setClassifications] = useState<DashCorretorClassificationRef[]>([])
  const [savedCategories, setSavedCategories] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set())
  const [savedProducaoMinima, setSavedProducaoMinima] = useState<number | null>(null)
  const [producaoMinimaInput, setProducaoMinimaInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isHelpdeskAdmin, setIsHelpdeskAdmin] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    setSaveError(null)

    try {
      const [refs, categories, admin, producaoMinima] = await Promise.all([
        dashcorretorService.listClassificationReferences(),
        portaisAtendimentoService.getFilaLigacaoCategorias(),
        portaisAtendimentoService.isCurrentUserHelpdeskAdmin(),
        portaisAtendimentoService.getFilaLigacaoProducaoMinima(),
      ])

      setClassifications(refs)
      setSavedCategories(categories)
      setSelectedCategories(new Set(categories))
      setSavedProducaoMinima(producaoMinima)
      setProducaoMinimaInput(formatProducaoInput(producaoMinima))
      setIsHelpdeskAdmin(admin)
    } catch (error) {
      setLoadError(getErrorMessage(error))
      setClassifications([])
      setSavedCategories([])
      setSelectedCategories(new Set())
      setSavedProducaoMinima(null)
      setProducaoMinimaInput('')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const selectedList = useMemo(() => Array.from(selectedCategories), [selectedCategories])

  const parsedProducaoMinima = useMemo(
    () => parseFilaLigacaoProducaoMinima(producaoMinimaInput),
    [producaoMinimaInput]
  )

  const isCategoriesDirty = useMemo(
    () => !areCategoryListsEqual(selectedList, savedCategories),
    [selectedList, savedCategories]
  )

  const isProducaoDirty = useMemo(
    () => parsedProducaoMinima !== savedProducaoMinima,
    [parsedProducaoMinima, savedProducaoMinima]
  )

  const isDirty = isCategoriesDirty || isProducaoDirty

  const classificationColorMap = useMemo(
    () => buildClassificationColorMap(classifications),
    [classifications]
  )

  const toggleCategory = (name: string) => {
    if (!isHelpdeskAdmin) return
    setSelectedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
    setSaveError(null)
  }

  const handleSave = async () => {
    if (!isHelpdeskAdmin || !isDirty) return

    setSaving(true)
    setSaveError(null)

    try {
      if (isCategoriesDirty) {
        const saved = await portaisAtendimentoService.saveFilaLigacaoCategorias(selectedList)
        const categories = await portaisAtendimentoService.getFilaLigacaoCategorias()

        await logPortaisAtendimentoUpdate(
          saved.id,
          'Alterou categorias habilitadas na fila automática de ligação',
          { categorias: savedCategories },
          { categorias: categories },
          {
            entidade: 'helpdesk_parameters',
            parameter_key: PORTAIS_ATENDIMENTO_FILA_LIGACAO_CATEGORIAS_KEY,
          }
        )

        setSavedCategories(categories)
        setSelectedCategories(new Set(categories))
      }

      if (isProducaoDirty) {
        const saved = await portaisAtendimentoService.saveFilaLigacaoProducaoMinima(parsedProducaoMinima)
        const producaoMinima = await portaisAtendimentoService.getFilaLigacaoProducaoMinima()

        await logPortaisAtendimentoUpdate(
          saved.id,
          'Alterou produção mínima na fila automática de ligação',
          { producao_minima: savedProducaoMinima },
          { producao_minima: producaoMinima },
          {
            entidade: 'helpdesk_parameters',
            parameter_key: PORTAIS_ATENDIMENTO_FILA_LIGACAO_PRODUCAO_MINIMA_KEY,
          }
        )

        setSavedProducaoMinima(producaoMinima)
        setProducaoMinimaInput(formatProducaoInput(producaoMinima))
      }
    } catch (error) {
      const message = getErrorMessage(error)
      if (message.toLowerCase().includes('permission') || message.toLowerCase().includes('policy')) {
        setSaveError('Sem permissão para alterar parâmetros. É necessário ser administrador do helpdesk.')
      } else {
        setSaveError(message)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="px-6 py-4 space-y-4">
        {!isHelpdeskAdmin && !loading && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 px-6 py-3">
            <Text size="sm" className="text-amber-800 dark:text-amber-200">
              Você pode visualizar esta configuração, mas apenas administradores do helpdesk podem alterá-la.
            </Text>
          </div>
        )}

        {loadError && (
          <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-6 py-4">
            <Text size="sm" className="text-red-700 dark:text-red-300">{loadError}</Text>
          </div>
        )}

        {saveError && (
          <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-6 py-4">
            <Text size="sm" className="text-red-700 dark:text-red-300">{saveError}</Text>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
              Categorias habilitadas
            </Heading>
            <Text size="sm" color="muted" className="mt-2">
              Selecione as categorias que devem entrar na fila para ligação.
            </Text>
          </div>

          <div className="px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : classifications.length === 0 ? (
              <Text size="sm" color="muted" className="py-8 text-center block">
                Nenhuma categoria encontrada em dashcorretor_classifications.
              </Text>
            ) : (
              <>
                <div className="flex flex-nowrap items-center gap-3 overflow-x-auto pb-1">
                  {classifications.map((classification) => {
                    const cardStyle = getClassificationCardStyleFromMap(
                      classification.name,
                      classificationColorMap
                    )
                    const hasColor = cardStyle != null

                    return (
                    <label
                      key={classification.name}
                      style={cardStyle}
                      className={`flex shrink-0 items-center gap-2 rounded-md px-3 py-2 ${
                        hasColor
                          ? 'border-0 text-white shadow-md'
                          : 'border border-gray-200 dark:border-gray-700'
                      } ${
                        isHelpdeskAdmin
                          ? hasColor
                            ? 'cursor-pointer hover:opacity-90'
                            : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          : 'opacity-80 cursor-not-allowed'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.has(classification.name)}
                        onChange={() => toggleCategory(classification.name)}
                        disabled={!isHelpdeskAdmin || saving}
                        aria-label={`Selecionar ${classification.name}`}
                      />
                      <span
                        className={`text-sm font-semibold ${
                          hasColor ? 'text-white' : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {classification.name}
                      </span>
                    </label>
                    )
                  })}
                </div>

                <Text size="sm" className="mt-6 text-gray-700 dark:text-gray-300">
                  {selectedCategories.size}{' '}
                  {selectedCategories.size === 1 ? 'categoria selecionada' : 'categorias selecionadas'}
                </Text>
              </>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
              Corretores com produção maior que
            </Heading>
            <Text size="sm" color="muted" className="mt-2">
              Apenas corretores com produção acima deste valor entram na fila automática. Deixe em branco para salvar
              sem número (sem filtro).
            </Text>
          </div>

          <div className="px-6 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="max-w-xs">
                <Input
                  type="text"
                  inputMode="decimal"
                  value={producaoMinimaInput}
                  onChange={(e) => {
                    const raw = e.target.value
                    if (raw === '' || /^\d*[.,]?\d*$/.test(raw)) {
                      setProducaoMinimaInput(raw)
                      setSaveError(null)
                    }
                  }}
                  disabled={!isHelpdeskAdmin || saving}
                  placeholder=""
                  aria-label="Corretores com produção maior que"
                />
              </div>
            )}
          </div>
        </div>

        {isHelpdeskAdmin && !loading && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleSave()}
              disabled={saving || !isDirty}
            >
              {saving ? 'Salvando...' : 'Salvar configuração'}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PortaisAtendimentoFilaAutomaticaTab
